import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import EngagementTrackerService from "../../services/EngagementTrackerService";
import PageHeading from "../common/page_heading/PageHeading";
import Loader from "../common/loader/Loader";
import EditFormModal from "./EditFormModal";
import TrackerRow from "./TrackerRow";
import ClientGroupHeader from "./ClientGroupHeader";
import EngagementSubGrids from "./EngagementSubGrids";
import { BackofficeStrip, BackofficeBand, BackofficeDivider, BackofficeRow, BackofficeTasks } from "./BackofficeTrackerRows";
import { attachBackofficeGroups, expandableKeys, normalizeMode, BACKOFFICE_MODES, BACKOFFICE_MODE_LABELS } from "./backofficeTracker";
import UserService from "../../services/UserService";
import UtilityService from "../../services/UtilityService";
import { buildOrderedColumns, reorderKeys, colKeyForNative, colKeyForField } from "./columnOrder";
import { PAGE_SIZE, AUTO_LOAD_MAX, mergeGridPage } from "./gridPaging";
import { buildTrackerCsv, csvColumns, csvFileName } from "./trackerCsv";
import { NATIVE_COLUMNS_TASK } from "./subgridColumns";
import ColumnFilterMenu from "./ColumnFilterMenu";
import {
	activeColumnFilterCount,
	cellFilterValue,
	columnFilterType,
	isActiveColumnFilter,
	rowPassesColumnFilter,
} from "./columnFilters";

import {
	FilterBuilder,
	emptyTree,
	isEmpty,
	stripIds,
	assignIds,
	countRules,
	pruneIncompleteRules,
} from "../common/filters";
import { buildEngagementTrackerSchema } from "../../schemas/engagementTrackerSchema";

import "./engagement-tracker.css";

const EXPANDED_PREF_KEY = "et_expanded_clients";
const FILTERS_PREF_KEY = "et_adhoc_filters";
const COLUMN_WIDTHS_PREF_KEY = "et_column_widths";
const SUB_COLUMN_WIDTHS_PREF_KEY = "et_sub_column_widths";
const COLUMN_ORDER_PREF_KEY = "et_column_order";
const SUB_COLUMN_ORDER_PREF_KEY = "et_sub_column_order";
// Hidden columns per resource — { engagement: [colKey], task: [colKey] }. We store
// the HIDDEN set (not visible) so columns added later default to visible.
const HIDDEN_COLUMNS_PREF_KEY = "et_hidden_columns";
// Sort for the engagement grid — { colKey, dir: "asc"|"desc" } or null.
const SORT_PREF_KEY = "et_sort";
// Excel-style per-column filters — { colKey: { selected, condition } }.
const COLUMN_FILTERS_PREF_KEY = "et_column_filters";
// Search scope — "filtered" (within the filtered grid) | "all" (every engagement).
const SEARCH_SCOPE_PREF_KEY = "et_search_scope";
// Most rows an "all engagements" search shows; past that the user narrows the text.
const SEARCH_ALL_LIMIT = 1000;
const NO_CLIENT_KEY = "__no_client__";
const MIN_COL_WIDTH = 70;
const DEFAULT_CUSTOM_WIDTH = 220;

function _filterTreesEqual(a, b) {
	try {
		return JSON.stringify(stripIds(a || emptyTree())) === JSON.stringify(stripIds(b || emptyTree()));
	} catch (e) {
		return false;
	}
}

// All engagement columns that exist on the engagement form. Each can be shown or
// hidden per view via the Columns picker. `template` is excluded (creation-only).
// Read-only columns: `naming_convention` (editing it alone would desync the
// auto-name); name/client/flow/status/team are managed from the engagement
// screen itself, not inline from the tracker.
const NATIVE_COLUMNS = [
	{ key: "name", label: "Engagement Name", type: "text", width: 220, icon: "Type", readonly: true },
	{ key: "client_id", label: "Client", type: "client", width: 200, icon: "Briefcase", readonly: true },
	{ key: "flow_id", label: "Workflow Type", type: "flow", width: 170, icon: "GitBranch", readonly: true },
	{ key: "flow_state_id", label: "Status", type: "flow_state", width: 180, icon: "ChevronDown", readonly: true },
	{ key: "fiscal_year_end", label: "Year End", type: "date", width: 150, icon: "Calendar" },
	{ key: "date_in", label: "Date In", type: "date", width: 150, icon: "Calendar" },
	{ key: "due_date", label: "Due Date", type: "date", width: 150, icon: "Calendar" },
	{ key: "team_id", label: "Team", type: "team", width: 150, icon: "Users", readonly: true },
	{ key: "tax_estimate", label: "Tax Est.", type: "checkbox", width: 90, icon: "CheckSquare" },
	{ key: "notes", label: "Notes", type: "text", width: 200, icon: "FileText" },
	{ key: "naming_convention", label: "Naming Convention", type: "text", width: 170, icon: "Tag", readonly: true },
];

class EngagementTracker extends WComponent {
	constructor(props) {
		super(props);

		let expandedClients = new Set();
		try {
			const raw = localStorage.getItem(EXPANDED_PREF_KEY);
			if (raw) {
				const arr = JSON.parse(raw);
				if (Array.isArray(arr)) expandedClients = new Set(arr.map(String));
			}
		} catch (e) {
			// ignore
		}

		let filterTree = emptyTree();
		try {
			const raw = localStorage.getItem(FILTERS_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && parsed.type === "group") filterTree = assignIds(parsed);
			}
		} catch (e) {
			// ignore
		}

		let columnWidths = {};
		try {
			const raw = localStorage.getItem(COLUMN_WIDTHS_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && typeof parsed === "object") columnWidths = parsed;
			}
		} catch (e) {
			// ignore
		}

		let subColumnWidths = { task: {}, user: {} };
		try {
			const raw = localStorage.getItem(SUB_COLUMN_WIDTHS_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && typeof parsed === "object") {
					subColumnWidths = { task: parsed.task || {}, user: parsed.user || {} };
				}
			}
		} catch (e) {
			// ignore
		}

		let columnOrder = [];
		try {
			const raw = localStorage.getItem(COLUMN_ORDER_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (Array.isArray(parsed)) columnOrder = parsed;
			}
		} catch (e) {
			// ignore
		}

		let subColumnOrder = { task: [], user: [] };
		try {
			const raw = localStorage.getItem(SUB_COLUMN_ORDER_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && typeof parsed === "object") {
					subColumnOrder = { task: parsed.task || [], user: parsed.user || [] };
				}
			}
		} catch (e) {
			// ignore
		}

		let hiddenColumns = { engagement: [], task: [] };
		try {
			const raw = localStorage.getItem(HIDDEN_COLUMNS_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && typeof parsed === "object") {
					hiddenColumns = {
						engagement: Array.isArray(parsed.engagement) ? parsed.engagement : [],
						task: Array.isArray(parsed.task) ? parsed.task : [],
					};
				}
			}
		} catch (e) {
			// ignore
		}

		let sort = null;
		try {
			const raw = localStorage.getItem(SORT_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && parsed.colKey && (parsed.dir === "asc" || parsed.dir === "desc")) sort = parsed;
			}
		} catch (e) {
			// ignore
		}

		let columnFilters = {};
		try {
			const raw = localStorage.getItem(COLUMN_FILTERS_PREF_KEY);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && typeof parsed === "object") columnFilters = parsed;
			}
		} catch (e) {
			// ignore
		}

		let searchScope = "filtered";
		try {
			if (localStorage.getItem(SEARCH_SCOPE_PREF_KEY) === "all") searchScope = "all";
		} catch (e) {
			// ignore
		}

		this.state = {
			loading: true,
			forms: [],
			activeForm: null,
			// The active tracker's identity. A tracker now stores its own view config
			// and can be shared company-wide; these track the selected one.
			activeFormOwnerId: null,
			activeFormShared: "private",
			activeFormCanEdit: true,
			activeFormConfig: null, // saved config baseline (for the dirty/Save check)
			// Backoffice work next to engagements: "off" | "rolled" | "rows" (saved with the tracker).
			backofficeMode: "off",
			expandedBackoffice: new Set(), // client keys whose rolled-up strip is open
			expandedBackofficeRows: new Set(), // backoffice engagement ids whose tasks are open
			grid: null,
			// Rows arrive in pages (see gridPaging.js): `loadingMore` while further
			// pages stream in, `exporting` while a CSV pulls every remaining page.
			loadingMore: false,
			exporting: false,
			search: "",
			searchFocused: false, // the search box grows while focused or holding text
			searchScope, // "filtered" | "all" — see _isSearchingAll
			searchAll: null, // { query, grid, loading, failed } — server-wide results while scope is "all"
			showFormMenu: false,
			showCreatePrompt: false,
			newFormName: "",
			showEditModal: false,
			savingForm: false,
			expandedClients,
			columnWidths, // { colKey: px } overrides (colKey = "n_<key>" | "f_<fieldId>")
			subColumnWidths, // { task: {colKey: px}, user: {colKey: px} } — shared across sub-grids
			columnOrder, // [colKey] display order (main grid)
			subColumnOrder, // { task: [colKey], user: [colKey] }
			hiddenColumns, // { engagement: [colKey], task: [colKey] } — hidden per resource
			sort, // { colKey, dir } for the engagement grid, or null
			columnFilters, // { colKey: { selected, condition } } — Excel-style header filters
			filterMenu: null, // { colKey, left, top } — open header filter dropdown
			showColumnPicker: false,
			dragColKey: null, // colKey being dragged (main grid header)
			dragOverColKey: null,
			// Per-engagement expansion -> nested Task/User sub-grids (lazy-loaded)
			expandedEngagements: new Set(),
			subGrids: {}, // { `${engagementId}:${resource}`: data }
			subLoading: {}, // { `${engagementId}:${resource}`: true }
			// Filter state
			filterTree,                        // what's currently in the editor
			appliedFilterTree: filterTree,     // what was last sent to the server (for dirty/Apply state)
			showFilterPanel: !isEmpty(filterTree),
		};
	}

	_persistAdhocFilters(tree) {
		try {
			localStorage.setItem(FILTERS_PREF_KEY, JSON.stringify(stripIds(tree || emptyTree())));
		} catch (e) {
			// ignore
		}
	}

	// ---------- Column resize ----------

	_persistColumnWidths(map) {
		try {
			localStorage.setItem(COLUMN_WIDTHS_PREF_KEY, JSON.stringify(map || {}));
		} catch (e) {
			// ignore
		}
	}

	// Commit a sub-grid (task/user) column resize. Shared across every engagement's
	// sub-grid of that resource.
	onSubColResize = (resource, colKey, width) => {
		const prev = this.state.subColumnWidths || { task: {}, user: {} };
		const subColumnWidths = { ...prev, [resource]: { ...(prev[resource] || {}), [colKey]: width } };
		try {
			localStorage.setItem(SUB_COLUMN_WIDTHS_PREF_KEY, JSON.stringify(subColumnWidths));
		} catch (e) {
			// ignore
		}
		this.update({ subColumnWidths });
	};

	// Commit a sub-grid column reorder (full ordered colKey list for that resource).
	onSubColReorder = (resource, orderedKeys) => {
		const prev = this.state.subColumnOrder || { task: [], user: [] };
		const subColumnOrder = { ...prev, [resource]: orderedKeys };
		try {
			localStorage.setItem(SUB_COLUMN_ORDER_PREF_KEY, JSON.stringify(subColumnOrder));
		} catch (e) {
			// ignore
		}
		this.update({ subColumnOrder });
	};

	// ---------- Column visibility (show/hide native + custom per resource) ----------

	_persistHiddenColumns(hidden) {
		try {
			localStorage.setItem(HIDDEN_COLUMNS_PREF_KEY, JSON.stringify(hidden || { engagement: [], task: [] }));
		} catch (e) {
			// ignore
		}
	}

	toggleColumnPicker = () => {
		this.update({ showColumnPicker: !this.state.showColumnPicker });
	};

	closeColumnPicker = () => {
		this.update({ showColumnPicker: false });
	};

	// Toggle one column's visibility for a resource ("engagement" | "task").
	toggleColumnVisible = (resource, colKey) => {
		const prev = this.state.hiddenColumns || { engagement: [], task: [] };
		const list = prev[resource] || [];
		const next = list.indexOf(colKey) === -1 ? list.concat([colKey]) : list.filter(k => k !== colKey);
		const hiddenColumns = { ...prev, [resource]: next };
		this._persistHiddenColumns(hiddenColumns);
		this.update({ hiddenColumns });
	};

	// Show or hide ALL columns for a resource at once (from a colKey list).
	setColumnsVisibility = (resource, colKeys, visible) => {
		const prev = this.state.hiddenColumns || { engagement: [], task: [] };
		let next;
		if (visible) {
			next = (prev[resource] || []).filter(k => colKeys.indexOf(k) === -1);
		} else {
			const set = new Set(prev[resource] || []);
			colKeys.forEach(k => set.add(k));
			next = Array.from(set);
		}
		const hiddenColumns = { ...prev, [resource]: next };
		this._persistHiddenColumns(hiddenColumns);
		this.update({ hiddenColumns });
	};

	// ---------- Column sort (click a header to sort records) ----------

	_persistSort(sort) {
		try {
			if (sort) localStorage.setItem(SORT_PREF_KEY, JSON.stringify(sort));
			else localStorage.removeItem(SORT_PREF_KEY);
		} catch (e) {
			// ignore
		}
	}

	// Cycle a column's sort: none -> asc -> desc -> none.
	onSortColumn = colKey => {
		const cur = this.state.sort;
		let next;
		if (!cur || cur.colKey !== colKey) next = { colKey, dir: "asc" };
		else if (cur.dir === "asc") next = { colKey, dir: "desc" };
		else next = null;
		this._persistSort(next);
		this.update({ sort: next });
	};

	// Comparable value for a row + colKey ("n_<key>" | "f_<id>"). Resolves
	// dropdown ids to their labels so sorting reads naturally. null sorts last.
	_sortComparable(row, colKey) {
		const grid = this._activeGrid() || {};
		const native = row.native || {};
		if (colKey.indexOf("f_") === 0) {
			const fieldId = colKey.slice(2);
			const field = (grid.fields || []).find(f => String(f.id) === String(fieldId));
			const raw = row.values ? row.values[fieldId] : null;
			if (raw == null || raw === "") return null;
			const dsl = grid.datasource_labels && grid.datasource_labels[fieldId];
			if (dsl && dsl[raw] != null) return String(dsl[raw]).toLowerCase();
			if (field && field.field_type === "checkbox") return raw === "1" || raw === 1 || raw === true ? 1 : 0;
			const n = Number(raw);
			if (!isNaN(n)) return n;
			return String(raw).toLowerCase();
		}
		const key = colKey.slice(2);
		switch (key) {
			case "name":
				return (row.engagement_name || "").toLowerCase();
			case "client_id":
				return (row.client_name || "").toLowerCase();
			case "flow_id": {
				const f = (grid.flows || []).find(x => x.id === native.flow_id);
				return f ? (f.name || "").toLowerCase() : null;
			}
			case "flow_state_id":
				return native.flow_state ? (native.flow_state.name || "").toLowerCase() : null;
			case "team_id": {
				const t = (grid.teams || []).find(x => x.id === native.team_id);
				return t ? (t.name || "").toLowerCase() : null;
			}
			case "tax_estimate":
				return native.tax_estimate ? 1 : 0;
			case "due_date":
			case "date_in":
			case "fiscal_year_end": {
				const v = native[key];
				if (!v) return null;
				const d = new Date(v);
				return isNaN(d.getTime()) ? null : d.getTime();
			}
			default:
				return native[key] != null && native[key] !== "" ? String(native[key]).toLowerCase() : null;
		}
	}

	_compareRows(a, b, sort) {
		const va = this._sortComparable(a, sort.colKey);
		const vb = this._sortComparable(b, sort.colKey);
		// Empty/missing values always sort to the end, regardless of direction.
		if (va == null && vb == null) return 0;
		if (va == null) return 1;
		if (vb == null) return -1;
		let cmp;
		if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
		else cmp = String(va).localeCompare(String(vb));
		return sort.dir === "desc" ? -cmp : cmp;
	}

	// ---------- Excel-style per-column filters (header funnel dropdowns) ----------

	_persistColumnFilters(map) {
		try {
			localStorage.setItem(COLUMN_FILTERS_PREF_KEY, JSON.stringify(map || {}));
		} catch (e) {
			// ignore
		}
	}

	// All column descriptors (native + custom) keyed by colKey.
	_descriptorByKey() {
		const fields = (this.state.grid && this.state.grid.fields) || [];
		const byKey = {};
		buildOrderedColumns(NATIVE_COLUMNS, fields, this.state.columnOrder).forEach(d => (byKey[d.colKey] = d));
		return byKey;
	}

	// Open/close the filter dropdown for one header. Position is fixed (viewport
	// coordinates) so the menu isn't clipped by the grid's horizontal scroll.
	openColumnFilterMenu = (e, d) => {
		e.stopPropagation();
		e.preventDefault();
		if (this.state.filterMenu && this.state.filterMenu.colKey === d.colKey) {
			this.update({ filterMenu: null });
			return;
		}
		const rect = e.currentTarget.getBoundingClientRect();
		const MENU_W = 300;
		const left = Math.max(8, Math.min(rect.left, window.innerWidth - MENU_W - 8));
		const top = Math.min(rect.bottom + 4, window.innerHeight - 120);
		this.update({ filterMenu: { colKey: d.colKey, left, top } });
	};

	closeColumnFilterMenu = () => {
		this.update({ filterMenu: null });
	};

	// Commit one column's filter ({ selected, condition } or null to clear).
	onApplyColumnFilter = (colKey, filter) => {
		const next = { ...(this.state.columnFilters || {}) };
		if (filter && isActiveColumnFilter(filter)) next[colKey] = filter;
		else delete next[colKey];
		this._persistColumnFilters(next);
		this.update({ columnFilters: next });
	};

	// Sort from the filter menu — sets an explicit direction (unlike the
	// header-click cycle).
	onSortFromFilterMenu = (colKey, dir) => {
		const next = { colKey, dir };
		this._persistSort(next);
		this.update({ sort: next });
	};

	clearAllColumnFilters = () => {
		this._persistColumnFilters({});
		this.update({ columnFilters: {}, filterMenu: null });
	};

	// Apply every active column filter to `rows`. `excludeColKey` leaves one
	// column's own filter out — its checklist lists the values visible under
	// all OTHER filters (Excel behavior).
	_applyColumnFilters(rows, excludeColKey) {
		const filters = this.state.columnFilters || {};
		const grid = this.state.grid;
		if (!grid) return rows;
		const activeKeys = Object.keys(filters).filter(
			k => k !== excludeColKey && isActiveColumnFilter(filters[k]),
		);
		if (!activeKeys.length) return rows;
		const byKey = this._descriptorByKey();
		const allRows = grid.rows || [];
		const entries = activeKeys
			.map(k => {
				const d = byKey[k];
				if (!d) return null; // stale filter for a removed column
				return { d, filter: filters[k], type: columnFilterType(d, allRows) };
			})
			.filter(Boolean);
		if (!entries.length) return rows;
		return rows.filter(row => entries.every(en => rowPassesColumnFilter(row, en.d, en.filter, grid, en.type)));
	}

	// Distinct display values for one column's checklist, with row counts.
	// Computed over rows that pass the search + every other column's filter.
	_distinctValuesFor(d, type) {
		const grid = this._activeGrid();
		if (!grid) return { values: [], hasBlanks: false, blanksCount: 0 };
		const rows = this._applyColumnFilters(this._searchedRows(), d.colKey);
		const counts = new Map();
		const isos = new Map(); // display -> ISO date (for chronological sorting)
		let blanksCount = 0;
		for (const row of rows) {
			const v = cellFilterValue(row, d, grid);
			if (v === "") {
				blanksCount++;
				continue;
			}
			counts.set(v, (counts.get(v) || 0) + 1);
			if (type === "date" && !isos.has(v)) {
				const native = row.native || {};
				const raw = d.kind === "custom" ? row.values && row.values[d.field.id] : native[d.col.key];
				if (raw) isos.set(v, String(raw).substr(0, 10));
			}
		}
		const values = Array.from(counts.keys()).map(v => ({ value: v, count: counts.get(v), iso: isos.get(v) || null }));
		return { values, hasBlanks: blanksCount > 0, blanksCount };
	}

	// ---------- Main grid column reorder (drag & drop headers) ----------

	_persistColumnOrder(order) {
		try {
			localStorage.setItem(COLUMN_ORDER_PREF_KEY, JSON.stringify(order || []));
		} catch (e) {
			// ignore
		}
	}

	onColDragStart = (e, colKey) => {
		if (this._resize) {
			e.preventDefault();
			return;
		}
		this._dragKey = colKey;
		try {
			e.dataTransfer.effectAllowed = "move";
			e.dataTransfer.setData("text/plain", colKey);
		} catch (err) {
			// ignore
		}
		this.update({ dragColKey: colKey });
	};

	onColDragOver = (e, colKey) => {
		if (!this._dragKey) return;
		e.preventDefault();
		try {
			e.dataTransfer.dropEffect = "move";
		} catch (err) {
			// ignore
		}
		if (this.state.dragOverColKey !== colKey) this.update({ dragOverColKey: colKey });
	};

	onColDrop = (e, colKey) => {
		e.preventDefault();
		const dragKey = this._dragKey;
		this._dragKey = null;
		if (!dragKey || dragKey === colKey) {
			this.update({ dragColKey: null, dragOverColKey: null });
			return;
		}
		const fields = (this.state.grid && this.state.grid.fields) || [];
		const orderedKeys = buildOrderedColumns(NATIVE_COLUMNS, fields, this.state.columnOrder).map(d => d.colKey);
		const next = reorderKeys(orderedKeys, dragKey, colKey);
		this._persistColumnOrder(next);
		this.update({ columnOrder: next, dragColKey: null, dragOverColKey: null });
	};

	onColDragEnd = () => {
		this._dragKey = null;
		this.update({ dragColKey: null, dragOverColKey: null });
	};

	// CSS custom properties on the grid root drive every column's width, so a drag
	// updates one variable (no 250-row React re-render).
	_gridVarStyle() {
		const style = {};
		const w = this.state.columnWidths || {};
		Object.keys(w).forEach(k => {
			if (w[k]) style["--etw-" + k] = w[k] + "px";
		});
		return style;
	}

	_colWidth(colKey, fallback) {
		const w = this.state.columnWidths || {};
		return w[colKey] || fallback;
	}

	onColResizeStart = (e, colKey, currentWidth) => {
		e.preventDefault();
		e.stopPropagation();
		this._resize = { colKey, startX: e.clientX, startWidth: currentWidth, lastWidth: currentWidth };
		document.addEventListener("mousemove", this._onColResizeMove);
		document.addEventListener("mouseup", this._onColResizeEnd);
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
	};

	_onColResizeMove = e => {
		if (!this._resize) return;
		const dx = e.clientX - this._resize.startX;
		const w = Math.max(MIN_COL_WIDTH, Math.round(this._resize.startWidth + dx));
		this._resize.lastWidth = w;
		// Imperative: update only the CSS variable — header + all body cells reflow.
		if (this._gridRef) this._gridRef.style.setProperty("--etw-" + this._resize.colKey, w + "px");
	};

	_onColResizeEnd = () => {
		document.removeEventListener("mousemove", this._onColResizeMove);
		document.removeEventListener("mouseup", this._onColResizeEnd);
		document.body.style.cursor = "";
		document.body.style.userSelect = "";
		const r = this._resize;
		this._resize = null;
		if (!r) return;
		const columnWidths = { ...this.state.columnWidths, [r.colKey]: r.lastWidth };
		this._persistColumnWidths(columnWidths);
		this.update({ columnWidths });
	};

	// Reusable resize handle for a header cell.
	_resizeHandle(colKey, currentWidth) {
		return (
			<span
				className="et-grid__col-resizer"
				draggable={false}
				onMouseDown={e => this.onColResizeStart(e, colKey, currentWidth)}
				onDragStart={e => e.preventDefault()}
				onClick={e => e.stopPropagation()}
				title="Drag to resize"
			/>
		);
	}

	toggleFilterPanel = () => {
		this.update({ showFilterPanel: !this.state.showFilterPanel });
	};

	onFilterTreeChange = nextTree => {
		// Edit-only: update the editor state + persist, but DO NOT re-fetch the grid.
		// The user must click "Apply" to push these filters to the server.
		this._persistAdhocFilters(nextTree);
		this.update({ filterTree: nextTree });
	};

	applyFilters = async () => {
		// Snapshot the current editor tree as "applied" and re-load the grid.
		// WComponent.update returns a Promise (no callback arg) — await it then load.
		const tree = this.state.filterTree;
		await this.update({ appliedFilterTree: tree });
		if (this.state.activeForm) await this.loadGrid();
	};

	// ---------- Tracker-as-view: config snapshot / apply / dirty / persist ----------

	// The tracker's persistable view config, snapshotted from current state.
	_currentConfig() {
		return {
			filters: isEmpty(this.state.filterTree) ? null : stripIds(this.state.filterTree),
			sort: this.state.sort || null,
			columnWidths: this.state.columnWidths || {},
			columnOrder: this.state.columnOrder || [],
			hiddenColumns: this.state.hiddenColumns || { engagement: [], task: [] },
			columnFilters: this._activeColumnFiltersOnly(this.state.columnFilters),
			backofficeMode: normalizeMode(this.state.backofficeMode),
		};
	}

	// Strip inactive/empty entries so configs compare stably for the dirty check.
	_activeColumnFiltersOnly(map) {
		const src = map || {};
		const out = {};
		Object.keys(src).forEach(k => {
			if (isActiveColumnFilter(src[k])) out[k] = src[k];
		});
		return out;
	}

	// Normalize a saved config object to the same shape _currentConfig() produces,
	// so the two can be JSON-compared for the dirty check.
	_normalizeConfig(config) {
		const c = config || {};
		const hc = c.hiddenColumns && typeof c.hiddenColumns === "object" ? c.hiddenColumns : {};
		return {
			filters: c.filters && !isEmpty(c.filters) ? stripIds(c.filters) : null,
			sort: c.sort && c.sort.colKey ? c.sort : null,
			columnWidths: c.columnWidths && typeof c.columnWidths === "object" ? c.columnWidths : {},
			columnOrder: Array.isArray(c.columnOrder) ? c.columnOrder : [],
			hiddenColumns: { engagement: hc.engagement || [], task: hc.task || [] },
			columnFilters: this._activeColumnFiltersOnly(
				c.columnFilters && typeof c.columnFilters === "object" ? c.columnFilters : {},
			),
			backofficeMode: normalizeMode(c.backofficeMode),
		};
	}

	_isFormDirty() {
		if (!this.state.activeForm || !this.state.activeFormConfig) return false;
		try {
			return JSON.stringify(this._currentConfig()) !== JSON.stringify(this.state.activeFormConfig);
		} catch (e) {
			return false;
		}
	}

	// Select a tracker: apply its saved config to the working state, set its
	// identity (owner/sharing), snapshot the baseline, then load the grid.
	_activateForm = async form => {
		const normalized = this._normalizeConfig(form.config);
		const filterTree = normalized.filters ? assignIds(normalized.filters) : emptyTree();
		this._persistAdhocFilters(filterTree);
		this._persistSort(normalized.sort);
		this._persistColumnWidths(normalized.columnWidths);
		this._persistColumnOrder(normalized.columnOrder);
		this._persistHiddenColumns(normalized.hiddenColumns);
		this._persistColumnFilters(normalized.columnFilters);

		await this.update({
			activeForm: form.form_name,
			activeFormOwnerId: form.owner_user_id != null ? form.owner_user_id : null,
			activeFormShared: form.is_shared || "private",
			activeFormCanEdit: form.can_edit !== false,
			activeFormConfig: normalized,
			filterTree,
			appliedFilterTree: filterTree,
			sort: normalized.sort,
			columnWidths: normalized.columnWidths,
			columnOrder: normalized.columnOrder,
			hiddenColumns: normalized.hiddenColumns,
			columnFilters: normalized.columnFilters,
			backofficeMode: normalized.backofficeMode,
			filterMenu: null,
			showFilterPanel: !isEmpty(filterTree) || this.state.showFilterPanel,
			showFormMenu: false,
		});
		await this.loadGrid(form.form_name, form.owner_user_id);
	};

	// Persist the current filters/sort/layout onto the active tracker.
	onSaveForm = async () => {
		if (!this.state.activeForm) return;
		const config = this._currentConfig();
		await EngagementTrackerService.saveFormConfig({
			formName: this.state.activeForm,
			formOwnerId: this.state.activeFormOwnerId,
			config,
		});
		await this.update({ activeFormConfig: this._normalizeConfig(config) });
		this._refreshForms();
	};

	// Toggle company sharing for a tracker.
	onToggleShareForm = async form => {
		const next = form.is_shared === "company" ? "private" : "company";
		await EngagementTrackerService.setFormShared({
			formName: form.form_name,
			formOwnerId: form.owner_user_id,
			isShared: next,
		});
		const forms = await this._refreshForms();
		const patch = {};
		if (form.form_name === this.state.activeForm && String(form.owner_user_id) === String(this.state.activeFormOwnerId)) {
			patch.activeFormShared = next;
		}
		if (Object.keys(patch).length) await this.update(patch);
		return forms;
	};

	// Delete a tracker (removes its columns for everyone who could see it).
	onDeleteForm = async form => {
		const ok = window.confirm('Delete tracker "' + form.form_name + '"? This removes its columns and saved settings.');
		if (!ok) return;
		await EngagementTrackerService.deleteForm({ formName: form.form_name, formOwnerId: form.owner_user_id });
		const forms = await this._refreshForms();
		const wasActive = form.form_name === this.state.activeForm && String(form.owner_user_id) === String(this.state.activeFormOwnerId);
		if (wasActive) {
			if (forms.length) await this._activateForm(forms[0]);
			else await this.update({ activeForm: null, activeFormOwnerId: null, grid: null });
		}
	};

	_refreshForms = async () => {
		const forms = await EngagementTrackerService.listForms();
		await this.update({ forms });
		return forms;
	};

	_persistExpanded(set) {
		try {
			localStorage.setItem(EXPANDED_PREF_KEY, JSON.stringify(Array.from(set)));
		} catch (e) {
			// ignore
		}
	}

	toggleClient = clientId => {
		const key = clientId == null ? NO_CLIENT_KEY : String(clientId);
		const next = new Set(this.state.expandedClients);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		this._persistExpanded(next);
		this.update({ expandedClients: next });
	};

	// ---------- Engagement row expansion -> nested Task/User sub-grids ----------

	toggleEngagement = engagementId => {
		const next = new Set(this.state.expandedEngagements);
		if (next.has(engagementId)) {
			next.delete(engagementId);
			this.update({ expandedEngagements: next });
		} else {
			next.add(engagementId);
			this.update({ expandedEngagements: next });
			// Lazy-load both sub-grids on first expand.
			this._loadSubGrid(engagementId, "task");
			this._loadSubGrid(engagementId, "user");
		}
	};

	_subKey(engagementId, resource) {
		return engagementId + ":" + resource;
	}

	async _loadSubGrid(engagementId, resource, force) {
		const key = this._subKey(engagementId, resource);
		if (!force && this.state.subGrids[key]) return; // cached
		// Functional setState so concurrent task+user loads (and other engagements)
		// merge against the latest state instead of clobbering each other's flags.
		this.setState(prev => ({ subLoading: { ...prev.subLoading, [key]: true } }));
		const data = await EngagementTrackerService.getSubGrid({
			engagementId,
			resource,
			formName: this.state.activeForm,
			formOwnerId: this.state.activeFormOwnerId,
		});
		this.setState(prev => ({
			subGrids: { ...prev.subGrids, [key]: data || { fields: [], rows: [], roles: [] } },
			subLoading: { ...prev.subLoading, [key]: false },
		}));
	}

	_replaceSubRow(engagementId, resource, recordId, newRow) {
		const key = this._subKey(engagementId, resource);
		this.setState(prev => {
			const data = prev.subGrids[key];
			if (!data) return null;
			const rows = data.rows.map(r => (String(r.record_id) === String(recordId) ? { ...r, ...newRow } : r));
			// Merge any client label the updated row carries (e.g. client just changed).
			const client_labels = { ...(data.client_labels || {}), ...(newRow.client_labels || {}) };
			return { subGrids: { ...prev.subGrids, [key]: { ...data, rows, client_labels } } };
		});
	}

	// Native field edit on a task/user sub-row.
	onSubNativeChange = async (resource, engagementId, recordId, field, value) => {
		// Optimistic local update.
		const key = this._subKey(engagementId, resource);
		this.setState(prev => {
			const data = prev.subGrids[key];
			if (!data) return null;
			const rows = data.rows.map(r =>
				String(r.record_id) === String(recordId) ? { ...r, native: { ...r.native, [field]: value } } : r,
			);
			return { subGrids: { ...prev.subGrids, [key]: { ...data, rows } } };
		});

		let row;
		if (resource === "task") {
			if (field === "assignees") row = await EngagementTrackerService.patchTask({ taskId: recordId, assignedUsers: value });
			else row = await EngagementTrackerService.patchTask({ taskId: recordId, field, value });
		} else {
			row = await EngagementTrackerService.patchUser({ userId: recordId, field, value, engagementId });
		}
		if (row) this._replaceSubRow(engagementId, resource, recordId, row);
	};

	// Custom column edit on a task/user sub-row.
	onSubCustomChange = (resource, engagementId, recordId, fieldId, value) => {
		const key = this._subKey(engagementId, resource);
		this.setState(prev => {
			const data = prev.subGrids[key];
			if (!data) return null;
			const rows = data.rows.map(r =>
				String(r.record_id) === String(recordId) ? { ...r, values: { ...r.values, [fieldId]: value } } : r,
			);
			return { subGrids: { ...prev.subGrids, [key]: { ...data, rows } } };
		});
		EngagementTrackerService.upsertSubValue({ resource, recordId, fieldId, value, engagementId });
	};

	onAddSubRow = async (resource, engagementId, payload) => {
		const row = await EngagementTrackerService.addSubRow({ engagementId, resource, payload });
		if (!row) return;
		const key = this._subKey(engagementId, resource);
		this.setState(prev => {
			const data = prev.subGrids[key];
			if (!data) return null;
			// Merge any client label the new row carries so its Client cell renders
			// the name (e.g. the engagement's client, defaulted on the server).
			const client_labels = { ...(data.client_labels || {}), ...(row.client_labels || {}) };
			return {
				subGrids: { ...prev.subGrids, [key]: { ...data, rows: data.rows.concat([row]), client_labels } },
			};
		});
	};

	expandAllClients = () => {
		const grid = this._activeGrid();
		if (!grid) return;
		const next = new Set();
		for (const row of grid.rows) {
			next.add(row.client_id == null ? NO_CLIENT_KEY : String(row.client_id));
		}
		expandableKeys([], grid.backoffice, this.state.backofficeMode).forEach(k => next.add(k));
		this._persistExpanded(next);
		this.update({ expandedClients: next });
	};

	collapseAllClients = () => {
		const next = new Set();
		this._persistExpanded(next);
		this.update({ expandedClients: next });
	};


	// Apply a row transform to the loaded grid AND the "all engagements" search
	// results, so an edit made while searching everything is still there after
	// switching back (and vice versa). Untouched rows keep referential identity.
	_patchRowsEverywhere(patchRow) {
		const { grid, searchAll } = this.state;
		const patch = {};
		if (grid && grid.rows) patch.grid = { ...grid, rows: grid.rows.map(patchRow) };
		if (searchAll && searchAll.grid && searchAll.grid.rows) {
			patch.searchAll = { ...searchAll, grid: { ...searchAll.grid, rows: searchAll.grid.rows.map(patchRow) } };
		}
		if (Object.keys(patch).length) this.update(patch);
	}

	// Stable 3-arg signature (see comment on onTrackerCellChange).
	onNativeCellChange = (engagementId, field, value) => {
		const grid = this._activeGrid();
		if (!grid) return;

		// Client cell passes { id, name } so we can update the row's client label
		// inline; every other field passes a scalar.
		const isClient = field === "client_id";
		const clientId = isClient ? (value && value.id != null ? value.id : null) : null;
		const clientName = isClient ? (value && value.name ? value.name : null) : null;

		const patchRow = row => {
			if (row.engagement_id !== engagementId) return row;
			const native = { ...row.native };
			let next = { ...row, native };
			if (field === "flow_state_id") {
				const fs = (grid.flow_states || []).find(s => s.id === value);
				native.flow_state_id = value;
				native.flow_state = fs ? { id: fs.id, name: fs.name, color: fs.color } : null;
			} else if (field === "flow_id") {
				// Changing flow clears the status (matches backend behavior).
				native.flow_id = value;
				native.flow_state_id = null;
				native.flow_state = null;
			} else if (isClient) {
				next.client_id = clientId;
				next.client_name = clientName;
			} else {
				native[field] = value;
			}
			if (field === "name") next.engagement_name = value || "(Untitled engagement)";
			return next;
		};

		this._patchRowsEverywhere(patchRow);
		EngagementTrackerService.patchEngagement({ engagementId, field, value: isClient ? clientId : value });
	};

	async componentDidMount() {
		// Document-level Tab handler is more reliable than React's bubbled onKeyDown
		// (avoids edge cases with rapid focus transitions on inputs being mounted/unmounted).
		document.addEventListener("keydown", this.onDocumentKeyDown, true);
		window.addEventListener("resize", this._syncTopScrollWidth);
		await this.loadForms();
	}

	componentDidUpdate() {
		// Keep the top scrollbar's phantom width matched to the grid's real scrollWidth
		// after every re-render (column changes, row changes, native-fields toggle, etc.).
		this._syncTopScrollWidth();
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.onDocumentKeyDown, true);
		window.removeEventListener("resize", this._syncTopScrollWidth);
		// Stop any page loop still streaming rows into the grid, and any pending search.
		this._loadToken = (this._loadToken || 0) + 1;
		clearTimeout(this._searchAllTimer);
		this._searchAllToken = (this._searchAllToken || 0) + 1;
	}

	// Mirror the grid's horizontal scrollWidth onto the phantom strip so the
	// top scrollbar's thumb matches the bottom one's range exactly.
	_syncTopScrollWidth = () => {
		if (!this._gridRef || !this._topScrollInnerRef) return;
		const w = this._gridRef.scrollWidth;
		if (this._topScrollInnerRef.style.width !== w + "px") {
			this._topScrollInnerRef.style.width = w + "px";
		}
	};

	// Bidirectional scroll sync. The `_syncingScroll` flag breaks the feedback
	// loop (scrolling A sets B, which would otherwise fire onScroll on B and
	// set A back — visible as jitter on touchpads).
	onGridScroll = () => {
		if (this._syncingScroll) return;
		if (!this._topScrollRef || !this._gridRef) return;
		this._syncingScroll = true;
		this._topScrollRef.scrollLeft = this._gridRef.scrollLeft;
		window.requestAnimationFrame(() => {
			this._syncingScroll = false;
		});
	};

	onTopScroll = () => {
		if (this._syncingScroll) return;
		if (!this._topScrollRef || !this._gridRef) return;
		this._syncingScroll = true;
		this._gridRef.scrollLeft = this._topScrollRef.scrollLeft;
		window.requestAnimationFrame(() => {
			this._syncingScroll = false;
		});
	};

	loadForms = async () => {
		await this.update({ loading: true });

		let forms = await EngagementTrackerService.listForms();

		if (forms.length > 0) {
			// Keep the current selection if it's still present, else default to most recent.
			let form = this.state.activeForm
				? forms.find(
						f =>
							f.form_name === this.state.activeForm &&
							(this.state.activeFormOwnerId == null ||
								String(f.owner_user_id) === String(this.state.activeFormOwnerId)),
				  )
				: null;
			if (!form) form = forms[0];
			await this.update({ forms });
			await this._activateForm(form);
		} else {
			await this.update({ forms, activeForm: null, activeFormOwnerId: null, grid: null, loading: false });
		}
	};

	// Schema is needed both for the filter panel UI and for pruning incomplete
	// rules out of the request payload (so the backend doesn't see a half-built
	// "Status / Equals / (no value)" rule and abort the whole filter).
	_buildSchema = grid =>
		buildEngagementTrackerSchema({
			trackerFields: (grid && grid.fields) || [],
			taskFields: (grid && grid.task_fields) || [],
			memberFields: (grid && grid.member_fields) || [],
		});

	loadGrid = async (formName, ownerId) => {
		const targetForm = formName || this.state.activeForm;
		if (!targetForm) return;
		const targetOwner = ownerId !== undefined ? ownerId : this.state.activeFormOwnerId;

		// Every load gets a token; page loops from an earlier load see it change and stop.
		this._loadToken = (this._loadToken || 0) + 1;
		const token = this._loadToken;

		await this.update({ loading: true, loadingMore: false });

		// Use the *previously loaded* grid's fields to build the schema for
		// pruning. On the very first load there's no grid yet — pruning still
		// works for native fields (always known) but may keep an in-progress
		// tracker rule. The backend's defensive try/catch covers that edge.
		const schema = this._buildSchema(this.state.grid);
		const pruned = pruneIncompleteRules(this.state.filterTree, schema);
		const filterTreeForServer = isEmpty(pruned) ? null : stripIds(pruned);

		// Later pages must ask for exactly what the first page asked for, even if
		// the user edits (but does not apply) the filters while they stream in.
		this._pageRequest = {
			formName: targetForm,
			formOwnerId: targetOwner,
			filterTree: filterTreeForServer,
			backofficeMode: normalizeMode(this.state.backofficeMode),
		};

		let page = await EngagementTrackerService.getGrid({ ...this._pageRequest, offset: 0, limit: PAGE_SIZE });
		if (token !== this._loadToken) return; // superseded by a newer load

		const grid = page ? mergeGridPage(null, page) : null;
		const patch = { grid: grid || { form_name: targetForm, fields: [], rows: [] }, loading: false };
		// Keep tracker identity in sync with the server (sharing can change elsewhere).
		if (grid) {
			if (grid.form_owner_id != null) patch.activeFormOwnerId = grid.form_owner_id;
			if (grid.is_shared) patch.activeFormShared = grid.is_shared;
			patch.activeFormCanEdit = grid.can_edit !== false;
		}
		await this.update(patch);

		// Keep pulling pages in the background so the grid holds the whole result
		// set (up to AUTO_LOAD_MAX; past that the row-count strip offers more).
		this._loadPages({ mode: "auto" });
	};

	// ---------- Paging: stream the remaining pages onto the loaded grid ----------

	/**
	 * Fetch further pages onto the current grid.
	 *   mode "auto" — keep going until done or AUTO_LOAD_MAX rows are in
	 *   mode "one"  — fetch a single page
	 *   mode "all"  — keep going until the server says there is no more
	 * Only one loop runs at a time; a later caller waits for the running one and
	 * then continues from wherever it stopped (ending at once if nothing is left
	 * to do). A newer loadGrid cancels whatever loop was running.
	 */
	_loadPages = async ({ mode } = {}) => {
		while (this._pageLoop) await this._pageLoop;
		const token = this._loadToken;
		const request = this._pageRequest;
		const run = async () => {
			let pagesFetched = 0;
			for (;;) {
				const grid = this.state.grid;
				if (!grid || !grid.has_more || !request || token !== this._loadToken) break;
				if (mode === "one" && pagesFetched >= 1) break;
				if (mode === "auto" && (grid.rows || []).length >= AUTO_LOAD_MAX) break;
				if (!this.state.loadingMore) await this.update({ loadingMore: true });

				const page = await EngagementTrackerService.getGrid({ ...request, offset: grid.fetched || 0, limit: PAGE_SIZE });
				pagesFetched++;
				if (token !== this._loadToken) break;
				if (!page) break; // request failed — keep what we have; the strip still offers "load more"
				await this.update({ grid: mergeGridPage(this.state.grid, page) });
			}
			if (token === this._loadToken && this.state.loadingMore) await this.update({ loadingMore: false });
		};
		this._pageLoop = run().finally(() => {
			this._pageLoop = null;
		});
		await this._pageLoop;
	};

	onLoadMore = () => this._loadPages({ mode: "one" });

	onLoadAll = () => this._loadPages({ mode: "all" });

	// The engagement columns the grid currently shows, in display order.
	_visibleColumns() {
		const fields = (this.state.grid && this.state.grid.fields) || [];
		const hiddenEng = (this.state.hiddenColumns && this.state.hiddenColumns.engagement) || [];
		return buildOrderedColumns(NATIVE_COLUMNS, fields, this.state.columnOrder).filter(d => hiddenEng.indexOf(d.colKey) === -1);
	}

	// ---------- CSV export ----------

	// Download the grid as the user sees it — every engagement matching the applied
	// filters (all pages are pulled first), then the search box and column filters,
	// grouped by client, with the visible columns in their display order.
	onExportCsv = async () => {
		if (this.state.exporting || !this.state.grid) return;
		await this.update({ exporting: true });
		try {
			const searchingAll = this._isSearchingAll();
			if (!searchingAll) await this._loadPages({ mode: "all" });
			const grid = this._activeGrid();
			if (!grid) return;
			if (grid.has_more) {
				window.alert(
					searchingAll
						? "Too many engagements match this search to export them all. Narrow the search text and try again."
						: "Not every engagement could be loaded, so the CSV was not created. Please try again.",
				);
				return;
			}
			const groups = this.groupedRows().filter(g => !g.backofficeOnly);
			const { headers, rows } = buildTrackerCsv({ groups, columns: csvColumns(this._visibleColumns()), grid });
			// The BOM makes Excel read the file as UTF-8 (accented client names survive).
			const csv = "﻿" + UtilityService.buildCsv({ headers, rows });
			UtilityService.downloadFile({
				blob: new Blob([csv], { type: "text/csv;charset=utf-8" }),
				fileName: csvFileName(this.state.activeForm),
			});
		} finally {
			await this.update({ exporting: false });
		}
	};

	onSelectForm = async form => {
		await this._activateForm(form);
	};

	onToggleFormMenu = () => {
		this.update({ showFormMenu: !this.state.showFormMenu });
	};

	onOpenCreatePrompt = () => {
		this.update({ showFormMenu: false, showCreatePrompt: true, newFormName: "" });
	};

	onCloseCreatePrompt = () => {
		this.update({ showCreatePrompt: false, newFormName: "" });
	};

	onNewFormNameChange = e => {
		this.update({ newFormName: e.target.value });
	};

	onConfirmCreateForm = async () => {
		let name = (this.state.newFormName || "").trim();
		if (!name) return;
		await this.update({ savingForm: true });

		// Seed every new form with a sensible default column so the grid isn't empty.
		const seedFields = [
			{
				field_name: "Status",
				field_type: "dropdown",
				options: [
					{ key: "in_progress", label: "In progress", color: "#cfe9c8" },
					{ key: "completed", label: "Completed", color: "#a8dca0" },
					{ key: "onboarding", label: "Onboarding", color: "#f7d0c1" },
					{ key: "under_review", label: "Under Review", color: "#f4b6a0" },
					{ key: "not_applicable", label: "Not applicable", color: "#dfe3eb" },
				],
				order_index: 0,
			},
			{ field_name: "Notes", field_type: "text", order_index: 1 },
		];

		const form = await EngagementTrackerService.createForm({ formName: name, fields: seedFields });
		await this.update({ savingForm: false, showCreatePrompt: false, newFormName: "" });

		if (form && form.form_name) {
			const forms = await this._refreshForms();
			const created = forms.find(f => f.form_name === form.form_name && f.is_owner) || forms.find(f => f.form_name === form.form_name);
			if (created) await this._activateForm(created);
		}
	};

	onOpenEditModal = () => {
		this.update({ showEditModal: true, showFormMenu: false });
	};

	onCloseEditModal = async (shouldReload = false) => {
		await this.update({ showEditModal: false });
		if (shouldReload && this.state.activeForm) {
			// Column changes may affect task/user sub-grids — drop their cache and
			// refetch any currently-expanded engagements with the new columns.
			const expanded = Array.from(this.state.expandedEngagements);
			await this.update({ subGrids: {}, subLoading: {} });
			// Refresh column definitions without re-applying the saved config (keeps
			// the user's current filters/sort/layout tweaks intact).
			await this._refreshForms();
			await this.loadGrid();
			expanded.forEach(engId => {
				this._loadSubGrid(engId, "task", true);
				this._loadSubGrid(engId, "user", true);
			});
		}
	};

	// Stable 3-arg signature so TrackerRow can keep referential equality on this prop
	// (avoids cascading PureComponent re-renders).
	onTrackerCellChange = (engagementId, fieldId, value) => {
		const grid = this._activeGrid();
		if (!grid) return;

		// Changing a parent datasource column invalidates its dependent children's
		// stored ids — clear them in the same row (and persist the clears).
		const childFieldIds = (grid.fields || [])
			.filter(f => f.datasource && String(f.datasource.parentFieldId) === String(fieldId))
			.map(f => f.id);
		const prevRow = grid.rows.find(r => r.engagement_id === engagementId);

		// Only the changed row is allocated anew — other rows keep referential identity,
		// so their <TrackerRow /> PureComponent skips re-rendering.
		const patchRow = row => {
			if (row.engagement_id !== engagementId) return row;
			const nextValues = { ...row.values, [fieldId]: value };
			childFieldIds.forEach(cid => {
				if (nextValues[cid]) nextValues[cid] = "";
			});
			return { ...row, values: nextValues };
		};

		this._patchRowsEverywhere(patchRow);
		EngagementTrackerService.upsertValue({ fieldId, engagementId, value });
		childFieldIds.forEach(cid => {
			if (prevRow && prevRow.values[cid]) {
				EngagementTrackerService.upsertValue({ fieldId: cid, engagementId, value: "" });
			}
		});
	};

	// ---------- Search: within the filtered results, or across all engagements ----------

	_persistSearchScope(scope) {
		try {
			localStorage.setItem(SEARCH_SCOPE_PREF_KEY, scope);
		} catch (e) {
			// ignore
		}
	}

	// True while the box holds text and the scope is "all": the grid then shows
	// the server-wide search results instead of the filtered grid.
	_isSearchingAll() {
		return this.state.searchScope === "all" && (this.state.search || "").trim().length > 0;
	}

	// The grid the page currently works from: the "all engagements" results while
	// searching everything (an empty grid until the first answer), else the loaded grid.
	_activeGrid() {
		const { grid, searchAll } = this.state;
		if (!this._isSearchingAll()) return grid;
		if (searchAll && searchAll.grid) return searchAll.grid;
		return grid ? { ...grid, rows: [], backoffice: null, total_count: 0, has_more: false } : null;
	}

	onSearchChange = e => {
		const search = e.target.value;
		this.update({ search });
		if (this.state.searchScope === "all") {
			this._scheduleSearchAll(search);
			return;
		}
		// The search runs over loaded rows, so make sure every matching engagement
		// is in before the user concludes "it's not there".
		if (search.trim() && this.state.grid && this.state.grid.has_more) this._loadPages({ mode: "all" });
	};

	onSetSearchScope = scope => {
		if (scope === this.state.searchScope) return;
		this._persistSearchScope(scope);
		this.update({ searchScope: scope, filterMenu: null });
		const q = (this.state.search || "").trim();
		if (scope === "all") {
			if (q) this._scheduleSearchAll(this.state.search, true);
		} else {
			this._cancelSearchAll();
			if (q && this.state.grid && this.state.grid.has_more) this._loadPages({ mode: "all" });
		}
		if (this._searchInputRef) this._searchInputRef.focus();
	};

	// Debounced server-wide search (engagement or client name contains the text),
	// ignoring the tracker's filter rules and column filters. Results stand in for
	// the grid while the text stays; a newer keystroke discards an older answer.
	_scheduleSearchAll(search, immediate) {
		clearTimeout(this._searchAllTimer);
		const q = (search || "").trim();
		if (!q) {
			this._cancelSearchAll();
			return;
		}
		this._searchAllTimer = setTimeout(() => this._runSearchAll(q), immediate ? 0 : 250);
	}

	_cancelSearchAll() {
		clearTimeout(this._searchAllTimer);
		this._searchAllToken = (this._searchAllToken || 0) + 1;
		if (this.state.searchAll) this.update({ searchAll: null });
	}

	async _runSearchAll(q) {
		if (!this.state.activeForm) return;
		this._searchAllToken = (this._searchAllToken || 0) + 1;
		const token = this._searchAllToken;
		// Keep the previous answer on screen while the new one is on its way.
		await this.update({ searchAll: { ...(this.state.searchAll || {}), query: q, loading: true, failed: false } });
		const page = await EngagementTrackerService.getGrid({
			formName: this.state.activeForm,
			formOwnerId: this.state.activeFormOwnerId,
			filterTree: null,
			backofficeMode: "off",
			search: q,
			offset: 0,
			limit: SEARCH_ALL_LIMIT,
		});
		if (token !== this._searchAllToken) return; // superseded by a newer keystroke or a scope change
		await this.update({ searchAll: { query: q, grid: page ? mergeGridPage(null, page) : null, loading: false, failed: !page } });
	}

	onSearchFocus = () => this.update({ searchFocused: true });

	onSearchBlur = () => this.update({ searchFocused: false });

	// Escape clears the search and leaves the box; the clear button just clears it.
	onSearchKeyDown = e => {
		if (e.key !== "Escape") return;
		e.preventDefault();
		this.update({ search: "" });
		this._cancelSearchAll();
		if (this._searchInputRef) this._searchInputRef.blur();
	};

	onClearSearch = () => {
		this.update({ search: "" });
		this._cancelSearchAll();
		if (this._searchInputRef) this._searchInputRef.focus();
	};

	focusSearch = () => {
		if (this._searchInputRef) this._searchInputRef.focus();
	};

	// Rows passing the toolbar search only (column filters not yet applied).
	_searchedRows = () => {
		const grid = this._activeGrid();
		const { search } = this.state;
		if (!grid) return [];
		let q = (search || "").trim().toLowerCase();
		if (!q) return grid.rows;
		return grid.rows.filter(r => {
			let client = (r.client_name || "").toLowerCase();
			let engagement = (r.engagement_name || "").toLowerCase();
			return client.indexOf(q) !== -1 || engagement.indexOf(q) !== -1;
		});
	};

	// Column filters apply within the filtered grid; a search across all
	// engagements ignores them (and the filter rules) by design.
	filteredRows = () => {
		const rows = this._searchedRows();
		return this._isSearchingAll() ? rows : this._applyColumnFilters(rows, null);
	};

	/**
	 * Group rows by client_id. Sorted alphabetically; "(No client)" goes last.
	 * Returns: [{ key, client_id, client_name, client_type, engagements: [] }]
	 */
	groupedRows = () => {
		const rows = this.filteredRows();
		const groupsByKey = new Map();
		for (const r of rows) {
			const key = r.client_id == null ? NO_CLIENT_KEY : String(r.client_id);
			let g = groupsByKey.get(key);
			if (!g) {
				g = {
					key,
					client_id: r.client_id,
					client_name: r.client_name || "(No client)",
					client_type: r.client_type,
					engagements: [],
				};
				groupsByKey.set(key, g);
			}
			g.engagements.push(r);
		}
		const list = Array.from(groupsByKey.values());
		list.sort((a, b) => {
			if (a.key === NO_CLIENT_KEY) return 1;
			if (b.key === NO_CLIENT_KEY) return -1;
			return (a.client_name || "").localeCompare(b.client_name || "");
		});

		// Apply the active column sort to engagements WITHIN each client group
		// (the grid stays grouped by client; records sort inside the group).
		const sort = this.state.sort;
		if (sort && sort.colKey) {
			list.forEach(g => g.engagements.sort((a, b) => this._compareRows(a, b, sort)));
		}
		const activeGrid = this._activeGrid();
		return attachBackofficeGroups(list, activeGrid && activeGrid.backoffice, this.state.backofficeMode, this.state.search);
	};

	isExpanded = group => {
		// Auto-expand all matching groups while searching so users see results immediately.
		if ((this.state.search || "").trim().length > 0) return true;
		return this.state.expandedClients.has(group.key);
	};

	// ---------- Backoffice work next to engagements (W-1105) ----------

	// "off" | "rolled" | "rows"; saved with the tracker like filters and columns.
	onSetBackofficeMode = async mode => {
		const next = normalizeMode(mode);
		if (next === this.state.backofficeMode) return;
		await this.update({ backofficeMode: next });
		await this.loadGrid();
	};

	toggleBackoffice = key => {
		const next = new Set(this.state.expandedBackoffice);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		this.update({ expandedBackoffice: next });
	};

	toggleBackofficeRow = id => {
		const next = new Set(this.state.expandedBackofficeRows);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		this.update({ expandedBackofficeRows: next });
	};

	// What a client group shows for its backoffice work: one expandable strip (rolled up) or one
	// muted row per current engagement (rows). Read-only either way.
	renderBackoffice(group, orderedHeaders) {
		const mode = normalizeMode(this.state.backofficeMode);
		if (!group.backoffice) return null;
		if (mode === "rolled") {
			const open = this.state.expandedBackoffice.has(group.key);
			return (
				<React.Fragment>
					<BackofficeStrip group={group} expanded={open} onToggle={this.toggleBackoffice} />
					{open && <BackofficeBand group={group} />}
				</React.Fragment>
			);
		}
		if (mode === "rows") {
			const current = group.backoffice.current || [];
			if (!current.length) return null;
			return (
				<React.Fragment>
					<BackofficeDivider group={group} />
					{current.map(e => {
						const open = this.state.expandedBackofficeRows.has(e.id);
						return (
							<React.Fragment key={e.id}>
								<BackofficeRow engagement={e} orderedColumns={orderedHeaders} expanded={open} onToggle={this.toggleBackofficeRow} />
								{open && <BackofficeTasks engagement={e} />}
							</React.Fragment>
						);
					})}
				</React.Fragment>
			);
		}
		return null;
	}

	/**
	 * Document-level Tab handler — keeps focus moving *within an engagement row*.
	 *
	 * Walks elements tagged with `data-tracker-focus` inside the closest
	 * `[data-row-key]` ancestor. At a row boundary, jumps to the first focusable
	 * of the next engagement row (skipping the engagement-name link + group
	 * headers, which the browser would otherwise stop on).
	 *
	 * Shift+Tab does the reverse. Outside the tracker grid, default browser tab
	 * behavior is untouched.
	 *
	 * Attached in capture phase so we beat any nested keydown handlers and can
	 * reliably preventDefault before the browser shifts focus.
	 */
	onDocumentKeyDown = e => {
		if (e.key !== "Tab") return;

		const target = e.target;
		if (!target || typeof target.closest !== "function") return;

		// Only intercept when focus is actually on one of our cell controls.
		const rowEl = target.closest("[data-row-key]");
		if (!rowEl) return;
		if (!this._gridWrapRef || !this._gridWrapRef.contains(rowEl)) return;

		// We only handle Tab when the focused element is a tagged cell control.
		// Otherwise the user is on the engagement link or a group header — leave default Tab.
		const isCellFocus =
			target.hasAttribute && target.hasAttribute("data-tracker-focus");
		if (!isCellFocus) return;

		const inRow = Array.from(rowEl.querySelectorAll("[data-tracker-focus]:not([disabled])"));
		const idx = inRow.indexOf(target);
		if (idx === -1) return;

		const goingForward = !e.shiftKey;
		const nextInRow = goingForward ? inRow[idx + 1] : inRow[idx - 1];

		if (nextInRow) {
			e.preventDefault();
			nextInRow.focus();
			return;
		}

		// At a row boundary — jump to first/last focusable in the next/previous row.
		const allRows = Array.from(this._gridWrapRef.querySelectorAll("[data-row-key]"));
		const rowIdx = allRows.indexOf(rowEl);
		const targetRow = goingForward ? allRows[rowIdx + 1] : allRows[rowIdx - 1];
		if (!targetRow) return;

		const focusables = targetRow.querySelectorAll("[data-tracker-focus]:not([disabled])");
		if (!focusables.length) return;

		e.preventDefault();
		const next = goingForward ? focusables[0] : focusables[focusables.length - 1];
		next.focus();
	};

	_renderFormItem(f) {
		const active = f.form_name === this.state.activeForm && String(f.owner_user_id) === String(this.state.activeFormOwnerId);
		return (
			<div key={f.owner_user_id + ":" + f.form_name} className={"et-form-picker__item " + (active ? "et-form-picker__item--active" : "")}>
				<button className="et-form-picker__item-main" onClick={() => this.onSelectForm(f)}>
					<Icon.Grid size={13} className="et-form-picker__item-icon" />
					<span className="et-form-picker__item-name">{f.form_name}</span>
					{f.is_shared === "company" && <span className="et-form-picker__badge">Shared</span>}
					{active && <Icon.Check size={13} className="et-form-picker__item-check" />}
				</button>
				{f.can_edit && (
					<span className="et-form-picker__item-actions">
						<button
							className="et-form-picker__act"
							title={f.is_shared === "company" ? "Make private" : "Share with team"}
							onClick={e => {
								e.stopPropagation();
								this.onToggleShareForm(f);
							}}
						>
							{f.is_shared === "company" ? <Icon.Users size={12} /> : <Icon.Share2 size={12} />}
						</button>
						<button
							className="et-form-picker__act et-form-picker__act--danger"
							title="Delete tracker"
							onClick={e => {
								e.stopPropagation();
								this.onDeleteForm(f);
							}}
						>
							<Icon.Trash2 size={12} />
						</button>
					</span>
				)}
			</div>
		);
	}

	renderFormPicker() {
		let { forms, activeForm, showFormMenu } = this.state;
		const mine = forms.filter(f => f.is_owner);
		const shared = forms.filter(f => !f.is_owner);

		return (
			<div className="et-form-picker">
				<button className="et-form-picker__trigger" onClick={this.onToggleFormMenu}>
					<Icon.Layers size={14} className="et-form-picker__icon" />
					<span className="et-form-picker__label">{activeForm || "Select a tracker"}</span>
					<Icon.ChevronDown size={14} className="et-form-picker__chevron" />
				</button>

				{showFormMenu && (
					<>
						<div className="et-form-picker__backdrop" onClick={this.onToggleFormMenu} />
						<div className="et-form-picker__menu">
							<div className="et-form-picker__menu-section-title">My Trackers</div>
							{mine.length === 0 && <div className="et-form-picker__empty">No trackers yet</div>}
							{mine.map(f => this._renderFormItem(f))}
							{shared.length > 0 && (
								<>
									<div className="et-form-picker__divider" />
									<div className="et-form-picker__menu-section-title">Shared with me</div>
									{shared.map(f => this._renderFormItem(f))}
								</>
							)}
							<div className="et-form-picker__divider" />
							<button className="et-form-picker__item et-form-picker__item--new" onClick={this.onOpenCreatePrompt}>
								<Icon.Plus size={13} className="et-form-picker__item-icon" />
								<span>New Tracker…</span>
							</button>
						</div>
					</>
				)}
			</div>
		);
	}

	renderToolbar() {
		let { activeForm, activeFormCanEdit, search, filterTree, showFilterPanel } = this.state;
		const ruleCount = countRules(filterTree);
		const colFilterCount = activeColumnFilterCount(this.state.columnFilters);
		const isDirty = this._isFormDirty();

		return (
			<div className="et-toolbar">
				<div className="et-toolbar__left">
					{this.renderFormPicker()}
					{activeForm && activeFormCanEdit && (
						<button
							className={"et-btn " + (isDirty ? "et-btn--primary" : "et-btn--ghost")}
							onClick={this.onSaveForm}
							disabled={!isDirty}
							title="Save the current filters, sort and column layout to this tracker"
						>
							<Icon.Save size={13} />
							<span>Save{isDirty ? " *" : ""}</span>
						</button>
					)}
				</div>

				<div className="et-toolbar__center">
					{/* Collapsed to an icon until it is focused or holds text, then it grows so
					    the typed engagement / client name is readable. */}
					<div
						className={"et-search ft-et-search " + (this.state.searchFocused || search ? "et-search--open" : "")}
						onClick={this.focusSearch}
						title="Search by engagement or client name"
					>
						<Icon.Search size={14} className="et-search__icon" />
						<input
							ref={el => (this._searchInputRef = el)}
							className="et-search__input"
							type="text"
							placeholder="Search by engagement or client name…"
							value={search}
							onChange={this.onSearchChange}
							onFocus={this.onSearchFocus}
							onBlur={this.onSearchBlur}
							onKeyDown={this.onSearchKeyDown}
							aria-label="Search engagements by engagement or client name"
						/>
						{search && (
							<button
								type="button"
								className="et-search__clear"
								onMouseDown={e => e.preventDefault()}
								onClick={this.onClearSearch}
								title="Clear search"
							>
								<Icon.X size={12} />
							</button>
						)}
					</div>
					{/* Where to search: inside the filtered results, or every engagement in the
					    company (filter rules and column filters set aside). */}
					{(this.state.searchFocused || search) && (
						<div
							className="et-search-scope ft-et-search-scope"
							role="group"
							aria-label="Search scope"
							title="Search within the filtered results, or across every engagement"
							onMouseDown={e => e.preventDefault()}
						>
							{[
								["filtered", "Filtered"],
								["all", "All"],
							].map(([scope, label]) => (
								<button
									key={scope}
									type="button"
									className={"et-search-scope__btn " + (this.state.searchScope === scope ? "et-search-scope__btn--on" : "")}
									onClick={() => this.onSetSearchScope(scope)}
									data-search-scope={scope}
								>
									{label}
								</button>
							))}
						</div>
					)}
				</div>

				<div className="et-toolbar__right">
					{activeForm && (
						<>
							<button
								className={
									"et-btn et-btn--ghost " + (showFilterPanel ? "et-btn--toggle-on" : "")
								}
								onClick={this.toggleFilterPanel}
								title="Show or hide the filter panel"
							>
								<Icon.Filter size={13} />
								<span>Filters{ruleCount > 0 ? " (" + ruleCount + ")" : ""}</span>
							</button>
							<div className="et-toolbar__btn-pair et-bo-mode ft-et-bo-mode" title="Show backoffice work next to engagements: off, one strip per client, or one row per current period">
								<span className="et-bo-mode__label">
									<Icon.Briefcase size={12} />
									<span>Backoffice</span>
								</span>
								{BACKOFFICE_MODES.map(m => (
									<button
										key={m}
										className={"et-btn et-btn--ghost et-bo-mode__btn " + (normalizeMode(this.state.backofficeMode) === m ? "et-btn--toggle-on" : "")}
										onClick={() => this.onSetBackofficeMode(m)}
										data-bo-mode={m}
									>
										{BACKOFFICE_MODE_LABELS[m]}
									</button>
								))}
							</div>
							{colFilterCount > 0 && (
								<button
									className="et-btn et-btn--ghost et-colfilter-chip"
									onClick={this.clearAllColumnFilters}
									title="Clear all column filters"
								>
									<Icon.Filter size={12} />
									<span>
										{colFilterCount} column{colFilterCount > 1 ? "s" : ""} filtered
									</span>
									<Icon.X size={12} className="et-colfilter-chip__x" />
								</button>
							)}
							<div className="et-toolbar__btn-pair">
								<button
									className="et-btn et-btn--ghost et-btn--pair-left"
									onClick={this.expandAllClients}
									title="Expand all clients"
								>
									<Icon.ChevronsDown size={13} />
									<span>Expand all</span>
								</button>
								<button
									className="et-btn et-btn--ghost et-btn--pair-right"
									onClick={this.collapseAllClients}
									title="Collapse all clients"
								>
									<Icon.ChevronsUp size={13} />
									<span>Collapse all</span>
								</button>
							</div>
							<div className="et-colpicker-wrap">
								<button
									className={"et-btn et-btn--ghost " + (this.state.showColumnPicker ? "et-btn--toggle-on" : "")}
									onClick={this.toggleColumnPicker}
									title="Show or hide columns"
								>
									<Icon.Sliders size={13} />
									<span>Columns</span>
								</button>
								{this.renderColumnPicker()}
							</div>
							<button className="et-btn et-btn--ghost" onClick={this.onOpenEditModal} title="Edit columns">
								<Icon.Settings size={13} />
								<span>Edit Columns</span>
							</button>
							<button
								className="et-btn et-btn--ghost ft-et-export"
								onClick={this.onExportCsv}
								disabled={this.state.exporting || !this.state.grid}
								title="Download every engagement matching the current filters as a CSV"
							>
								<Icon.Download size={13} />
								<span>{this.state.exporting ? "Exporting…" : "Export CSV"}</span>
							</button>
							<button className="et-btn et-btn--primary" onClick={this.onOpenCreatePrompt} title="Create a new form">
								<Icon.Plus size={13} />
								<span>New Form</span>
							</button>
						</>
					)}
				</div>
			</div>
		);
	}

	renderFilterPanel() {
		const { grid, filterTree, appliedFilterTree, showFilterPanel } = this.state;
		if (!showFilterPanel || !grid) return null;

		const schema = this._buildSchema(grid);
		const ctx = {
			flowStates: grid.flow_states,
			flows: grid.flows,
			teams: grid.teams,
			roles: grid.roles,
			fieldOptions: grid.field_options,
		};

		const hasPending = !_filterTreesEqual(filterTree, appliedFilterTree);
		const warnings = grid.filter_warnings || [];

		return (
			<div className="et-filter-panel-wrap">
				<FilterBuilder
					schema={schema}
					ctx={ctx}
					value={filterTree}
					hasPendingChanges={hasPending}
					onChange={this.onFilterTreeChange}
					onApply={this.applyFilters}
				/>
				{warnings.length > 0 && (
					<div className="et-filter-warnings">
						<Icon.AlertTriangle size={12} />
						<div>
							<strong>Some filter rules were skipped:</strong>
							<ul>
								{warnings.map((w, i) => (
									<li key={i}>{w}</li>
								))}
							</ul>
						</div>
					</div>
				)}
			</div>
		);
	}

	// Strip under the toolbar: how many engagements match, how many are loaded,
	// and — once auto-loading stops at AUTO_LOAD_MAX — a way to pull the rest.
	renderRowCount() {
		const { grid, loadingMore, exporting } = this.state;
		if (!grid) return null;
		const fmt = n => Number(n).toLocaleString();

		if (this._isSearchingAll()) {
			const sa = this.state.searchAll || {};
			const q = (this.state.search || "").trim();
			const results = sa.grid;
			if (sa.failed) {
				return (
					<div className="et-rowcount et-rowcount--partial ft-et-rowcount">
						<Icon.AlertTriangle size={12} />
						<span>The search across all engagements could not be run. Edit the text to try again.</span>
					</div>
				);
			}
			if (sa.loading || !results) {
				return (
					<div className="et-rowcount et-rowcount--busy ft-et-rowcount">
						<Icon.Loader size={12} className="et-rowcount__spin" />
						<span>Searching all engagements for “{q}”…</span>
					</div>
				);
			}
			const n = results.total_count != null ? results.total_count : (results.rows || []).length;
			return (
				<div className={"et-rowcount ft-et-rowcount " + (results.has_more ? "et-rowcount--partial" : "")}>
					{results.has_more && <Icon.AlertTriangle size={12} />}
					<span>
						{fmt(n)} engagement{n === 1 ? "" : "s"} match “{q}” across all engagements
					</span>
					<span className="et-rowcount__shown">· filter rules and column filters are set aside</span>
					{results.has_more && <span>· showing the first {fmt((results.rows || []).length)}, narrow the search to see the rest</span>}
				</div>
			);
		}

		const loaded = (grid.rows || []).length;
		const total = grid.total_count != null ? grid.total_count : loaded;
		const shown = this.filteredRows().length;
		const narrowed = shown !== loaded ? <span className="et-rowcount__shown">· {fmt(shown)} shown after search and column filters</span> : null;

		if (exporting) {
			return (
				<div className="et-rowcount et-rowcount--busy ft-et-rowcount">
					<Icon.Loader size={12} className="et-rowcount__spin" />
					<span>
						Preparing CSV… {fmt(loaded)} of {fmt(total)} engagements loaded
					</span>
				</div>
			);
		}
		if (loadingMore) {
			return (
				<div className="et-rowcount et-rowcount--busy ft-et-rowcount">
					<Icon.Loader size={12} className="et-rowcount__spin" />
					<span>
						Loading engagements… {fmt(loaded)} of {fmt(total)}
					</span>
					{narrowed}
				</div>
			);
		}
		if (grid.has_more) {
			return (
				<div className="et-rowcount et-rowcount--partial ft-et-rowcount">
					<Icon.AlertTriangle size={12} />
					<span>
						Showing {fmt(loaded)} of {fmt(total)} engagements. Narrow the filters, or
					</span>
					<button className="et-rowcount__link ft-et-load-more" onClick={this.onLoadMore}>
						load {fmt(Math.min(PAGE_SIZE, total - loaded))} more
					</button>
					<button className="et-rowcount__link ft-et-load-all" onClick={this.onLoadAll}>
						load all
					</button>
					{narrowed}
				</div>
			);
		}
		return (
			<div className="et-rowcount ft-et-rowcount">
				<span>
					{fmt(total)} engagement{total === 1 ? "" : "s"}
				</span>
				{narrowed}
			</div>
		);
	}

	renderEmptyState() {
		return (
			<div className="et-empty">
				<div className="et-empty__icon-wrap">
					<Icon.Grid size={28} />
				</div>
				<h2 className="et-empty__title">Build your first tracker</h2>
				<p className="et-empty__desc">
					Define your own columns — text, dropdowns, or checkboxes — and update every engagement from a single
					spreadsheet view. Each form is private to you.
				</p>
				<button className="et-btn et-btn--primary et-btn--lg" onClick={this.onOpenCreatePrompt}>
					<Icon.Plus size={14} />
					<span>Create your first form</span>
				</button>
			</div>
		);
	}

	// One header cell for an ordered column descriptor (native or custom),
	// draggable for reordering + resizable.
	renderColumnHeader(d) {
		const { dragColKey, dragOverColKey } = this.state;
		let iconEl;
		let label;
		let defaultW;
		let nativeClass = "";
		if (d.kind === "native") {
			const Ico = Icon[d.col.icon] || Icon.Square;
			iconEl = <Ico size={11} className="et-grid__th-icon" />;
			label = d.col.label;
			defaultW = d.col.width;
			nativeClass = " et-grid__th--native";
		} else {
			const f = d.field;
			iconEl =
				f.field_type === "dropdown" ? (
					<Icon.ChevronDown size={11} className="et-grid__th-icon" />
				) : f.field_type === "checkbox" ? (
					<Icon.CheckSquare size={11} className="et-grid__th-icon" />
				) : (
					<Icon.Type size={11} className="et-grid__th-icon" />
				);
			label = f.field_name;
			defaultW = DEFAULT_CUSTOM_WIDTH;
		}
		const basis = "var(--etw-" + d.colKey + ", " + defaultW + "px)";
		const sort = this.state.sort;
		const sortDir = sort && sort.colKey === d.colKey ? sort.dir : null;
		const isFiltered = isActiveColumnFilter((this.state.columnFilters || {})[d.colKey]);
		const menuOpen = this.state.filterMenu && this.state.filterMenu.colKey === d.colKey;
		const cls =
			"et-grid__th et-grid__th--resizable et-grid__th--draggable" +
			nativeClass +
			(sortDir ? " et-grid__th--sorted" : "") +
			(isFiltered ? " et-grid__th--filtered" : "") +
			(dragColKey === d.colKey ? " et-grid__th--dragging" : "") +
			(dragOverColKey === d.colKey && dragColKey && dragColKey !== d.colKey ? " et-grid__th--dragover" : "");
		return (
			<div
				key={d.colKey}
				className={cls}
				style={{ flex: "0 0 " + basis, width: basis }}
				title={label}
				draggable={true}
				onDragStart={e => this.onColDragStart(e, d.colKey)}
				onDragOver={e => this.onColDragOver(e, d.colKey)}
				onDrop={e => this.onColDrop(e, d.colKey)}
				onDragEnd={this.onColDragEnd}
			>
				<Icon.MoreVertical size={11} className="et-grid__th-grip" />
				{iconEl}
				<button
					type="button"
					className="et-grid__th-sort"
					onClick={() => this.onSortColumn(d.colKey)}
					title="Sort by this column"
				>
					<span className="et-grid__th-label">{label}</span>
					{sortDir === "asc" && <Icon.ArrowUp size={11} className="et-grid__th-sort-icon" />}
					{sortDir === "desc" && <Icon.ArrowDown size={11} className="et-grid__th-sort-icon" />}
				</button>
				<button
					type="button"
					className={
						"et-grid__th-filter" +
						(isFiltered ? " et-grid__th-filter--active" : "") +
						(menuOpen ? " et-grid__th-filter--open" : "")
					}
					onClick={e => this.openColumnFilterMenu(e, d)}
					onMouseDown={e => e.stopPropagation()}
					onDragStart={e => e.preventDefault()}
					draggable={false}
					title={isFiltered ? "Filtered — click to edit" : "Filter this column"}
				>
					<Icon.Filter size={10} />
				</button>
				{this._resizeHandle(d.colKey, this._colWidth(d.colKey, defaultW))}
			</div>
		);
	}

	// Column show/hide picker. Lists every native + custom column for the
	// engagement grid and the task sub-grid; checking a box toggles visibility.
	_pickerSection(resource, label, items) {
		const hidden = (this.state.hiddenColumns && this.state.hiddenColumns[resource]) || [];
		const allKeys = items.map(it => it.colKey);
		const allVisible = allKeys.every(k => hidden.indexOf(k) === -1);
		return (
			<div className="et-colpicker__section" key={resource}>
				<div className="et-colpicker__section-head">
					<span>{label}</span>
					<button
						className="et-colpicker__toggle-all"
						onClick={() => this.setColumnsVisibility(resource, allKeys, !allVisible)}
					>
						{allVisible ? "Hide all" : "Show all"}
					</button>
				</div>
				{items.map(it => {
					const checked = hidden.indexOf(it.colKey) === -1;
					return (
						<label
							key={it.colKey}
							className={"et-colpicker__item " + (it.locked ? "et-colpicker__item--locked" : "")}
						>
							<input
								type="checkbox"
								checked={checked}
								disabled={it.locked}
								onChange={() => !it.locked && this.toggleColumnVisible(resource, it.colKey)}
							/>
							<span>{it.label}</span>
							{it.custom && <span className="et-colpicker__badge">Custom</span>}
						</label>
					);
				})}
			</div>
		);
	}

	renderColumnPicker() {
		if (!this.state.showColumnPicker) return null;
		const grid = this.state.grid || {};
		const engFields = grid.fields || [];
		const taskFields = grid.task_fields || [];

		const engItems = NATIVE_COLUMNS.map(c => ({ colKey: colKeyForNative(c), label: c.label }))
			.concat(engFields.map(f => ({ colKey: colKeyForField(f), label: f.field_name, custom: true })));

		// Task anchor (the task name) is always shown — lock it on.
		const taskItems = NATIVE_COLUMNS_TASK.map(c => ({
			colKey: colKeyForNative(c),
			label: c.label,
			locked: !!c.anchor,
		})).concat(taskFields.map(f => ({ colKey: colKeyForField(f), label: f.field_name, custom: true })));

		return (
			<>
				<div className="et-colpicker__backdrop" onClick={this.closeColumnPicker} />
				<div className="et-colpicker">
					<div className="et-colpicker__title">
						<Icon.Sliders size={12} />
						<span>Show / hide columns</span>
					</div>
					{this._pickerSection("engagement", "Engagement", engItems)}
					{this._pickerSection("task", "Tasks", taskItems)}
				</div>
			</>
		);
	}

	// The open header filter dropdown (fixed-positioned, one at a time).
	renderColumnFilterMenu() {
		const fm = this.state.filterMenu;
		const grid = this._activeGrid();
		if (!fm || !grid) return null;
		const d = this._descriptorByKey()[fm.colKey];
		if (!d) return null;
		const type = columnFilterType(d, grid.rows || []);
		const { values, hasBlanks, blanksCount } = this._distinctValuesFor(d, type);
		const sort = this.state.sort;
		return (
			<ColumnFilterMenu
				key={fm.colKey}
				colKey={d.colKey}
				label={d.kind === "native" ? d.col.label : d.field.field_name}
				type={type}
				values={values}
				hasBlanks={hasBlanks}
				blanksCount={blanksCount}
				filter={(this.state.columnFilters || {})[fm.colKey] || null}
				sortDir={sort && sort.colKey === fm.colKey ? sort.dir : null}
				anchor={{ left: fm.left, top: fm.top }}
				onSort={this.onSortFromFilterMenu}
				onApply={this.onApplyColumnFilter}
				onClose={this.closeColumnFilterMenu}
			/>
		);
	}

	renderGrid() {
		const grid = this._activeGrid();
		if (!grid) return null;

		let rows = this.filteredRows();
		let groups = this.groupedRows();
		let fields = grid.fields || [];
		let datasourceLabels = grid.datasource_labels || {};
		let fieldsById = {};
		fields.forEach(f => (fieldsById[f.id] = f));
		let flowStates = grid.flow_states || [];
		let flows = grid.flows || [];
		let teams = grid.teams || [];

		const hiddenEng = (this.state.hiddenColumns && this.state.hiddenColumns.engagement) || [];
		const orderedHeaders = this._visibleColumns();

		return (
			<div className="et-grid-wrap" ref={el => (this._gridWrapRef = el)}>
				<div
					className="et-grid__top-scroll"
					ref={el => (this._topScrollRef = el)}
					onScroll={this.onTopScroll}
				>
					<div
						className="et-grid__top-scroll-inner"
						ref={el => (this._topScrollInnerRef = el)}
					/>
				</div>
				<div className="et-grid" ref={el => (this._gridRef = el)} onScroll={this.onGridScroll} style={this._gridVarStyle()}>
					{/* Column headers row — anchor (fixed) + freely reorderable columns */}
					<div className="et-grid__head">
						<div className="et-grid__th et-grid__th--anchor">
							<Icon.User size={12} className="et-grid__th-icon" />
							<span>Client / Engagement</span>
						</div>

						{orderedHeaders.map(d => this.renderColumnHeader(d))}

						{fields.length === 0 && (
							<div className="et-grid__th et-grid__th--placeholder">
								<button className="et-grid__add-col" onClick={this.onOpenEditModal}>
									<Icon.Plus size={12} />
									<span>Add a column</span>
								</button>
							</div>
						)}
					</div>

					<div className="et-grid__body">
						{rows.length === 0 && (
							<div className="et-grid__no-rows">
								<Icon.Inbox size={20} />
								<div>No engagements match your search or column filters.</div>
								{activeColumnFilterCount(this.state.columnFilters) > 0 && (
									<button className="et-btn et-btn--ghost" onClick={this.clearAllColumnFilters}>
										Clear column filters
									</button>
								)}
							</div>
						)}

						{groups.map(group => {
							const expanded = this.isExpanded(group);
							return (
								<React.Fragment key={group.key}>
									<ClientGroupHeader
										group={group}
										expanded={expanded}
										onToggle={this.toggleClient}
									/>
									{expanded &&
										group.engagements.map(row => {
											const engExpanded = this.state.expandedEngagements.has(row.engagement_id);
											const tKey = this._subKey(row.engagement_id, "task");
											const uKey = this._subKey(row.engagement_id, "user");
											return (
												<React.Fragment key={row.engagement_id}>
													<TrackerRow
														row={row}
														fields={fields}
														fieldsById={fieldsById}
														datasourceLabels={datasourceLabels}
														nativeColumns={NATIVE_COLUMNS}
														columnOrder={this.state.columnOrder}
														hiddenColumns={hiddenEng}
														flowStates={flowStates}
														flows={flows}
														teams={teams}
														isGrouped={true}
														expanded={engExpanded}
														onToggleExpand={this.toggleEngagement}
														onTrackerCellChange={this.onTrackerCellChange}
														onNativeCellChange={this.onNativeCellChange}
													/>
													{engExpanded && (
														<EngagementSubGrids
															engagementId={row.engagement_id}
															engagementName={row.engagement_name}
															taskData={this.state.subGrids[tKey]}
															userData={this.state.subGrids[uKey]}
															taskLoading={!!this.state.subLoading[tKey]}
															userLoading={!!this.state.subLoading[uKey]}
															isAdmin={UserService.isAdministrator()}
															subWidths={this.state.subColumnWidths}
															subOrder={this.state.subColumnOrder}
															hiddenTaskColumns={(this.state.hiddenColumns || {}).task || []}
															onColResize={this.onSubColResize}
															onColReorder={this.onSubColReorder}
															onNativeChange={this.onSubNativeChange}
															onCustomChange={this.onSubCustomChange}
															onAddRow={this.onAddSubRow}
														/>
													)}
												</React.Fragment>
											);
										})}
									{expanded && this.renderBackoffice(group, orderedHeaders)}
								</React.Fragment>
							);
						})}
					</div>
				</div>
				{this.renderColumnFilterMenu()}
			</div>
		);
	}

	renderCreatePrompt() {
		let { showCreatePrompt, newFormName, savingForm } = this.state;
		if (!showCreatePrompt) return null;

		return (
			<div className="et-modal-overlay" onClick={this.onCloseCreatePrompt}>
				<div className="et-modal et-modal--sm" onClick={e => e.stopPropagation()}>
					<div className="et-modal__header">
						<h2 className="et-modal__title">
							<Icon.Layers size={16} style={{ marginRight: 8 }} />
							New Form
						</h2>
						<button className="et-modal__close" onClick={this.onCloseCreatePrompt}>
							<Icon.X size={16} />
						</button>
					</div>
					<div className="et-modal__body">
						<label className="et-modal__label">Form name</label>
						<input
							className="et-modal__input"
							autoFocus
							placeholder="e.g. Bookkeeping Tracker"
							value={newFormName}
							onChange={this.onNewFormNameChange}
							onKeyDown={e => {
								if (e.key === "Enter") this.onConfirmCreateForm();
							}}
						/>
						<div className="et-modal__hint">
							You'll get a <strong>Status</strong> dropdown and a <strong>Notes</strong> field to start.
							You can add more columns after.
						</div>
					</div>
					<div className="et-modal__footer">
						<button className="et-btn et-btn--ghost" onClick={this.onCloseCreatePrompt} disabled={savingForm}>
							Cancel
						</button>
						<button
							className="et-btn et-btn--primary"
							onClick={this.onConfirmCreateForm}
							disabled={savingForm || !newFormName.trim()}
						>
							{savingForm ? "Creating…" : "Create form"}
						</button>
					</div>
				</div>
			</div>
		);
	}

	render() {
		let { loading, forms, activeForm, showEditModal, grid } = this.state;

		return (
			<div className="container et-page">
				<div className="et-page__heading-wrap">
					<PageHeading section="Workflow" title="Tracker" />
				</div>

				{this.renderToolbar()}

				{this.renderFilterPanel()}

				{loading && (
					<div className="et-loading">
						<Loader />
					</div>
				)}

				{!loading && forms.length === 0 && this.renderEmptyState()}

				{!loading && activeForm && this.renderRowCount()}

				{!loading && activeForm && this.renderGrid()}

				{this.renderCreatePrompt()}

				{showEditModal && grid && (
					<EditFormModal
						formName={activeForm}
						formOwnerId={this.state.activeFormOwnerId}
						fields={grid.fields}
						flowStates={grid.flow_states}
						onClose={this.onCloseEditModal}
					/>
				)}
			</div>
		);
	}
}

export default EngagementTracker;
